import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Heart, Image as ImageIcon, Plus, X } from "lucide-react";

export default function SafetyVault() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ kind: "text", title: "", content: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/vault").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!draft.title.trim() || !draft.content.trim()) return;
    setSaving(true);
    try {
      await api.post("/vault", draft);
      setDraft({ kind: "text", title: "", content: "" });
      toast.success("Added to your vault");
      await load();
    } catch {
      toast.error("Could not add");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/vault/${id}`);
    await load();
  };

  return (
    <div className="space-y-6 fade-up" data-testid="vault-page">
      <header>
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-rose-500" />
          <h1 className="font-display text-3xl font-bold text-slate-900">Personal safety vault</h1>
        </div>
        <p className="text-slate-600 mt-2 max-w-xl">Photos, messages and reasons that anchor you. We'll surface these when distress is high.</p>
      </header>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="vault-form">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })} className="border border-slate-300 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-teal-500" data-testid="vault-kind">
            <option value="text">Message / quote</option>
            <option value="image">Image (URL)</option>
          </select>
          <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Title (e.g. My son Lucas)" className="border border-slate-300 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-teal-500 md:col-span-1" data-testid="vault-title" />
          <input value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })} placeholder={draft.kind === "image" ? "https://image-url.jpg" : "What do you want to remember?"} className="border border-slate-300 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-teal-500 md:col-span-2" data-testid="vault-content" />
        </div>
        <button data-testid="vault-add" disabled={saving} onClick={add} className="mt-3 bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white rounded-xl px-4 py-2 flex items-center gap-1 text-sm font-semibold">
          <Plus className="w-4 h-4" /> Add to vault
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((it) => (
          <div key={it.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`vault-item-${it.id}`}>
            {it.kind === "image" ? (
              <img src={it.content} alt={it.title} className="w-full h-44 object-cover" />
            ) : (
              <div className="p-5 bg-gradient-to-br from-teal-50 to-amber-50">
                <p className="text-slate-800 italic leading-relaxed">"{it.content}"</p>
              </div>
            )}
            <div className="p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-900 flex items-center gap-2">
                  {it.kind === "image" && <ImageIcon className="w-4 h-4 text-slate-400" />}
                  {it.title}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">{new Date(it.created_at).toLocaleDateString("en-GB")}</div>
              </div>
              <button data-testid={`vault-remove-${it.id}`} onClick={() => remove(it.id)} className="text-slate-400 hover:text-red-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="md:col-span-2 bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-500" data-testid="vault-empty">
            Your vault is empty. Add a few reasons, people or messages that keep you anchored.
          </div>
        )}
      </div>
    </div>
  );
}
